<template>
  <div
    class="video-wrapper"
    :class="{hover: isHovering}"
    @mouseover="isHovering = true"
    @mouseout="isHovering = false"
  >
    <!-- Share overlay -->
    <share-overlay
      v-if="displayShareOverlay"
      :url="share.url"
      aria-hidden="true"
      tabindex="-1"
      @close="showShare"
    >
      <template #share-overlay-content>
        <p>{{ share.label }}</p>
        <div layout="row">
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.linkedin.com/shareArticle?url=' + share.url"
          >
            <c-icon
              name="linkedin"
              size="xl"
            />
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.twitter.com/intent/tweet?url=' + share.url"
          >
            <c-icon
              name="twitter"
              size="xl"
            />
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.facebook.com/sharer/sharer.php?u=' + share.url"
          >
            <c-icon
              name="facebook"
              size="xl"
            />
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            :href="'mailto:?Subject=' + share.emailSubject + '&amp;body=' + share.url"
          >
            <c-icon
              name="email"
              size="xl"
            />
          </a>
        </div>
        <copy-to-clipboard :text="share.url">
          <template #click-to-copy>
            <a
              class="button share-download-link"
              :content-download-url="share.url"
              content-download-type="video"
            >{{ share.buttonLabel }}</a>
          </template>
          <template #result>
            <p>{{ share.buttonResultText }}</p>
          </template>
        </copy-to-clipboard>
      </template>
    </share-overlay>

    <div
      v-if="!disableShare && !displayShareOverlay && share && share.url.length"
      class="gradientback"
      aria-hidden="true"
      tabindex="-1"
    >
      <a
        class="button button--header vt-video-share-button"
        @click="showShare"
      >{{ share.label }}</a>
    </div>

    <div
      v-definesize="autoSize"
      class="vt-video"
    >
      <div
        v-if="!autoplay"
        :class="['vt-video__poster lazyload', { 'vt-video__poster--active' : playing }]"
        :data-bgset="`${poster}?mw=800 [(max-width: 767px)] | ${poster}?mw=1280`"
      >
        <a
          v-if="player"
          class="button button--play"
          tabindex="0"
          @click="changeStatus"
          @keyup.enter="changeStatus"
        >
          <span class="sr-only">{{ srLabel }}</span>
          <svg>
            <use xlink:href="#icon-play" />
          </svg>
        </a>
      </div>
      <div
        v-show="loading"
        class="nt-loading nt-loading--block"
      >
        <div class="lds-ring">
          <div /><div /><div /><div />
        </div>
      </div>
      <div
        v-show="!loading"
        class="the-player"
        style="height: 100%"
      >
        <div :id="id" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref, watch, onMounted, onUnmounted
} from 'vue'
import Emitter from 'tiny-emitter'
import ShareOverlay from '@molecules/share-overlay/share-overlay.vue'

export default {
  name: 'VideoYoutube',
  components: {
    ShareOverlay
  },
  props: {
    autoplay: {
      type: Boolean,
      default: true
    },
    poster: {
      type: String,
      default: null,
      required: true
    },
    data: {
      type: String,
      default: null,
      required: true
    },
    autoSize: {
      type: Boolean
    },
    srLabel: {
      type: String,
      default: 'Video'
    },
    disableShare: {
      type: Boolean,
      default: false
    },
    share: {
      type: Object,
      defult: null
    }
  },
  setup(props) {
    const isHovering = ref(false)
    const playing = ref(false)
    const player = ref(null)
    const displayShareOverlay = ref(false)
    const loading = ref(true)

    const id = `video-${Math.floor(Math.random() * 100000)}`

    const changeStatus = () => {
      playing.value = !playing.value
    }

    const showShare = () => {
      displayShareOverlay.value = !displayShareOverlay.value
      if (displayShareOverlay.value) {
        playing.value = false
      }
    }

    const onPlayerReady = () => {
      if (props.autoplay) {
        playing.value = true
      }
      loading.value = false
    }

    const setupPlayer = () => {
      if (!window.YT) return
      player.value = new YT.Player(id, { // eslint-disable-line
        width: '100%',
        height: '100%',
        videoId: props.data,
        playerVars: {
          showinfo: 0,
          modestbranding: 1,
          rel: 0
        },
        events: {
          onReady: onPlayerReady
        }
      })
    }

    watch(playing, (isPlaying) => {
      if (player.value === null) return
      if (isPlaying) {
        player.value.playVideo()
      } else {
        player.value.pauseVideo()
      }
    })

    onMounted(() => {
      let setupAttempts = 0
      const interval = setInterval(() => {
        setupAttempts++
        setupPlayer()
        if (setupAttempts >= 10 || player.value !== null) clearInterval(interval)
      }, 250)
    })

    const handleChangeVideo = (payload) => {
      if (payload.id === id) {
        switch (payload.command) {
          case 'pause':
            if (!playing.value) return
            playing.value = false
            break
          case 'stop':
            if (playing.value) {
              player.value.stopVideo()
              playing.value = false
            }
            // player.value.seekTo(0)
            break
          default:
            console.warn(`Command ${payload.command} not found.`)
            break
        }
      }
    }

    const emitter = new Emitter()
    emitter.on('vt-change-video', handleChangeVideo)

    onUnmounted(() => {
      emitter.off('vt-change-video', handleChangeVideo)
    })

    return {
      isHovering,
      playing,
      player,
      displayShareOverlay,
      changeStatus,
      showShare,
      loading,
      id
    }
  }
}
</script>

<style lang="scss" scoped>
.vt-video {
  overflow: hidden;
  width: 100%;
  padding-bottom: calc(100%/1.8);

  .the-player {
    background: #000;
    border: 0;
    outline: none;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
  }

  &__poster {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: #f8f8f8;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    z-index: 1;
    transition: all 0.8s;

    &:hover {
      transform: scale(1.08);
    }

    & button {
      z-index: 2;
    }

    &--active {
      visibility: hidden;
    }
  }
}
</style>
